import { EyeTwoTone } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Result,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import PaqueryTable from '@paquery-team/lib-table';
import SITELINKS from 'constants/sitelinks';
import dayjs, { Dayjs } from 'dayjs';
import { TableDateTimeFormat } from 'constants/dateFormats';
import { useDrivers } from 'services/DriverService';
import { useHistoryRoutes } from 'services/journeys/list';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { Journey, JourneyKind } from 'interfaces/journey';
import useCollectionsEnabled from 'hooks/useCollectionsEnabled';
import { Option } from '..';
import { KindSelect, RouteSearch, kindColumn, nameColumn } from './kind';
import ProgressBar from '../components/progressBar';
import AuditLog from '../dashboard/components/AuditLog';

const idColumn = {
  title: 'Nro.',
  dataIndex: 'id',
  key: 'id',
  align: 'center',
  width: '8%',
};

const progressColumn = {
  title: 'Progreso',
  key: 'progress',
  width: '25%',
  responsive: ['sm'],
  render: (journey: Journey) => (
    <Flex gap={16}>
      <Tooltip
        title={
          (journey.totalCollections ?? 0) > 0
            ? `${journey.totalPackages ?? 0} envíos y ${journey.totalCollections} colectas`
            : 'Cantidad de envíos'
        }
      >
        {(journey.totalCollections ?? 0) > 0
          ? `${(journey.totalPackages ?? 0) + (journey.totalCollections ?? 0)}`
          : (journey.totalPackages ?? '---')}
      </Tooltip>
      <ProgressBar journey={journey} />
    </Flex>
  ),
};

const distributionDateColumn = {
  title: 'Fecha de inicio',
  key: 'date',
  dataIndex: 'startedDate',
  render: (date: Date) => dayjs(date).tz().format(TableDateTimeFormat),
  responsive: ['sm'],
};

const driverColumn = {
  title: 'Driver',
  key: 'driverName',
  dataIndex: 'driverName',
  render: (driverName: string) => driverName || 'Sin driver',
  responsive: ['sm'],
};

const detailColumn = {
  key: 'detail',
  dataIndex: 'id',
  render: (id: string) => {
    return (
      <Flex gap={8} justify="space-around">
        <Tooltip title="Detalle">
          <Link to={`${SITELINKS.journey}/${id}`}>
            <EyeTwoTone style={{ fontSize: 18 }} />
          </Link>
        </Tooltip>
        <AuditLog mode="list" journeyId={id} />
      </Flex>
    );
  },
  width: '7%',
  align: 'center',
};

const dataColumnsFor = (withKind: boolean) => [
  idColumn,
  nameColumn,
  ...(withKind ? [kindColumn] : []),
  progressColumn,
  driverColumn,
  distributionDateColumn,
  detailColumn,
];


const RoutesHistory = () => {
  const collectionsEnabled = useCollectionsEnabled();
  const dataColumns = dataColumnsFor(collectionsEnabled);
  const { data: drivers } = useDrivers();
  const [driverList, setDriverList] = useState<Option[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<number>();
  const [selectedKind, setSelectedKind] = useState<JourneyKind>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [date, setDate] = useState<Dayjs>();
  const [search, setSearch] = useState<string>();
  const queries = useMemo(
    () => ({
      driverId: selectedDriver,
      kind: selectedKind,
      date,
      page,
      size,
      search,
    }),
    [page, selectedDriver, selectedKind, date, size, search],
  );
  const { data, isLoading, isFetching, error, refetch } =
    useHistoryRoutes(queries);

  const pageable = useMemo(() => {
    return {
      // ponytail: el control lo maneja el estado local `page`; del server solo el total
      pageNumber: page,
      pageSize: size ?? DEFAULT_PAGINATE.pageSize,
      totalElements: data?.total_elements ?? DEFAULT_PAGINATE.totalElements,
    };
  }, [page, size, data]);

  const handleOnPaginate = (pagination: {
    pageNumber: number;
    pageSize: number;
  }) => {
    setPage(pagination.pageNumber);
    setSize(pagination.pageSize);
  };

  // ponytail: identidad estable, si no el effect del Searcher (allowEmptySearch)
  // dispara onSearching('') en cada render y resetea la página
  const handleSearch = useCallback((value?: string) => {
    setSearch(value || undefined);
    setPage(0);
  }, []);

  useEffect(() => {
    if (drivers && drivers.length > 0) {
      setDriverList(
        drivers.map(
          (driver: { id: number; name: string; lastName: string }) => ({
            value: driver.id,
            name: `${driver.name} ${driver.lastName}`,
          }),
        ),
      );
    }
  }, [drivers]);

  const getResultStatus = (err: AxiosError) => {
    if (!err.response?.status) return 'error';
    if (
      err.response.status === 404 ||
      err.response.status === 403 ||
      err.response.status === 500
    )
      return err.response.status;
    return 'error';
  };

  const extraButtons = [
    <Button type="primary" onClick={() => refetch()}>
      Reintentar
    </Button>,
  ];

  return (
    <Row gutter={[16, 36]}>
      <Col xs={24}>
        {error && (
          <Result
            status={getResultStatus(error as AxiosError)}
            title="Algo salio mal..."
            subTitle={error.message}
            extra={extraButtons}
          />
        )}
        {!error && (
          <PaqueryTable
            header={{
              extraMainAction: (
                <Space>
                  <RouteSearch onSearch={handleSearch} />
                  {collectionsEnabled && (
                    <KindSelect onChange={setSelectedKind} />
                  )}
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Driver"
                    style={{ width: 200 }}
                    options={driverList.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    onChange={(value: number) => setSelectedDriver(value)}
                  />
                  <DatePicker
                    placeholder="Seleccione fecha de inicio"
                    style={{ width: 220 }}
                    onChange={(date: Dayjs | null) =>
                      setDate(date ?? undefined)
                    }
                  />
                </Space>
              ),
              refresh: refetch,
              title: 'Historial',
            }}
            onChangePaginate={handleOnPaginate}
            loading={isLoading}
            fetching={isFetching}
            dataSource={
              data && data.journeys?.map((j) => ({ ...j, id: j.journeyId }))
            }
            dataColumns={dataColumns}
            paginate={pageable}
            colsForSmallDevice={dataColumns}
            colsForMediumDevice={dataColumns}
            colsForLargeDevice={dataColumns}
          />
        )}
      </Col>
    </Row>
  );
};

export default RoutesHistory;
